import { Filter, Test, TestSet } from '../types';
import {
  BaseSelectedVariant,
  SelectedVariant,
  SelectedShortVariant,
  SelectedStructuralVariant,
  SelectedExpressionVariant,
  SelectedCopyNumberVariant,
  BuildReferenceType,
} from './types';

export function reduceFilters(filters: Array<Filter>) {
  return filters.reduce((acc, opt) => {
    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    if (acc[opt.searchKey]) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      acc[opt.searchKey] = acc[opt.searchKey].concat(`,${opt.searchValue}`);
    } else {
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      acc[opt.searchKey] = opt.searchValue;
    }
    return acc;
  }, {});
}

interface FilterHandlerProps {
  onHashParamPush?: any;
  option: any;
  selectedFilterOptions: Array<any>;
  setSelectedFilterOptions: (newFilterOptions: any) => void;
}

export const handleFilterAdd = ({
  onHashParamPush,
  option,
  selectedFilterOptions,
  setSelectedFilterOptions,
}: FilterHandlerProps) => {
  // Handle array values
  if (Array.isArray(option)) {
    const currentFilterIds = selectedFilterOptions.map(f => f.id);

    // Ensure we only add ids that are not already added
    const newFiltersToAdd = [];
    for (const filter of option) {
      if (!currentFilterIds.includes(filter.id)) {
        newFiltersToAdd.push(filter);
      }
    }

    if (newFiltersToAdd.length > 0) {
      const newFilterState = [...selectedFilterOptions, ...newFiltersToAdd];
      setSelectedFilterOptions(newFilterState);
      onHashParamPush?.({
        filters: JSON.stringify(newFilterState),
      });
    }

    return;
  }

  // Handle individual option values
  const index = selectedFilterOptions.findIndex(opt => opt.id === option.id);
  if (index >= 0) {
    const newFilterState = [
      ...selectedFilterOptions.slice(0, index),
      option,
      ...selectedFilterOptions.slice(index + 1),
    ];

    setSelectedFilterOptions(newFilterState);
    onHashParamPush?.({
      filters: JSON.stringify(newFilterState),
    });
  } else {
    const newFilterState = [...selectedFilterOptions, option];

    setSelectedFilterOptions(newFilterState);
    onHashParamPush?.({
      filters: JSON.stringify(newFilterState),
    });
  }
};

export const handleFilterRemove = ({
  onHashParamPush,
  option,
  selectedFilterOptions,
  setSelectedFilterOptions,
}: FilterHandlerProps) => {
  const index = selectedFilterOptions.findIndex(opt => opt.id === option.id);

  if (index >= 0) {
    const newFilterState = [
      ...selectedFilterOptions.slice(0, index),
      ...selectedFilterOptions.slice(index + 1),
    ];

    setSelectedFilterOptions(newFilterState);
    newFilterState?.length >= 1
      ? onHashParamPush?.({
          filters: encodeURIComponent(JSON.stringify(newFilterState)),
        })
      : onHashParamPush?.({
          filters: undefined,
        });
  }
};

/**
 * @param rowData
 * @param aminoAcidField
 * @param variantSet
 * @param activeTest
 * @return variant data object from given variant set type.
 */
export const createVariantDataObject = (
  rowData: any,
  aminoAcidField: string,
  variantSet?: TestSet,
  activeTest?: Test,
): SelectedVariant => {
  const baseVariantData: BaseSelectedVariant = {
    variantId: rowData.id,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    testId: activeTest?.id,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    setType: variantSet?.setType,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    sequenceId: variantSet?.sequenceId,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    sequenceType: variantSet?.sequenceType,
    buildReference: activeTest?.referenceSetId as BuildReferenceType,
  };
  switch (variantSet?.setType) {
    case 'shortVariant': {
      return {
        ...baseVariantData,
        ncChange: rowData.ensemblCanon?.[0]?.nucleotideChange,
        aaChange: rowData?.ensemblCanon?.[0]?.[aminoAcidField],
        gene: rowData.ensemblCanon?.[0]?.gene,
        chromosome: rowData.chromosome,
        position: rowData.position,
        refAllele: rowData.reference,
        altAllele: rowData.alternate,
        alleleFrequency: rowData.vcf?.variantAllelicFrequency,
      } as SelectedShortVariant;
    }
    case 'structuralVariant': {
      return {
        ...baseVariantData,
        effect: rowData.type,
        geneId1: rowData.geneId1,
        geneId2: rowData.geneId2,
        chromosome1: rowData.chromosome1,
        chromosome2: rowData.chromosome2,
        startPosition1: rowData.startPosition1,
        endPosition1: rowData.endPosition1,
        startPosition2: rowData.startPosition2,
        endPosition2: rowData.endPosition2,
      } as SelectedStructuralVariant;
    }
    case 'expression': {
      return {
        ...baseVariantData,
        gene: rowData.gene,
        expression: rowData.expression,
        expressionUnit: rowData.expressionUnit,
      } as SelectedExpressionVariant;
    }
    case 'copyNumberVariant': {
      return {
        ...baseVariantData,
        geneId: rowData.geneId,
        chromosome: rowData.chromosome,
        startPosition: rowData.startPosition,
        endPosition: rowData.endPosition,
        type: rowData.status,
        copyNumberValue: rowData.copyNumber,
      } as SelectedCopyNumberVariant;
    }
    default:
      return baseVariantData;
  }
};
